import type { Client } from '../../api/client.js';
import { MESSAGE_MOTIF } from './editeur.js';
import { motifValide } from './parcours.js';
import type { DossierVue, EntreeJournal } from './types.js';

/**
 * ARB-DOSSIER-58 : « En tête du parcours ». Le contrat traite afterStepId null comme « à la fin » (T-L31-PRO-03, conservé,
 * journal/decisions.md) ; la tête s'obtient par un ajout PUIS un réordonnancement.
 */
export const EN_TETE = '__en_tete__';
export const MESSAGE_TETE_NON_PLACEE =
  'L’étape est ajoutée à la fin du parcours ; elle n’a pas pu être placée en tête. Utilisez « Monter » ou réessayez.';

export interface ReglageAppris {
  key: string;
  value: unknown;
  updatedAt: string;
  updatedByDisplayName: string;
}

export interface ActionsDossier {
  charger(): Promise<DossierVue>;
  /** Rôles de la personne qui lit l'écran (`auth.moi`) ; vide si la session est illisible. */
  rolesLectrice(): Promise<readonly string[]>;
  journal(): Promise<readonly EntreeJournal[]>;
  /** Réglage appris du courrier d'annonce (`beta.reglages`), ou null s'il n'a pas encore de réponse (lot 4, point 7). */
  courrierAnnonce(): Promise<ReglageAppris | null>;
  /** `apresEtapeId` : une étape, null (à la fin du parcours) ou EN_TETE (en tête : ajout puis réordonnancement). */
  ajouter(phase: string, libelle: string, apresEtapeId: string | null): Promise<void>;
  sauter(etapeId: string, motif: string): Promise<void>;
  reordonner(ids: readonly string[]): Promise<void>;
}

/**
 * Lectures et écritures de l’écran, uniquement par les routes du contrat. Chaque écriture porte son Idempotency-Key
 * (client) et son action d’audit (serveur) ; l’événement est émis par la route, jamais par l’écran.
 */
export function creerActions(client: Client, dossierId: string): ActionsDossier {
  const parametres = { dossierId };
  return {
    charger: () => client.appeler<DossierVue>('dossiers.lire', { parametres }),
    async rolesLectrice() {
      const session = await client.appeler<{
        user?: { roles?: { role: string }[] };
        effectiveUser?: { roles?: { role: string }[] } | null;
      }>('auth.moi');
      const personne = session?.effectiveUser ?? session?.user;
      return Array.isArray(personne?.roles) ? personne.roles.map((r) => r.role) : [];
    },
    async journal() {
      const page = await client.appeler<{ items?: EntreeJournal[] }>('dossiers.journal', { parametres });
      if (!page || !Array.isArray(page.items)) throw new Error('Le journal du dossier est indisponible. Réessayez.');
      return page.items;
    },
    async courrierAnnonce() {
      const reglages = await client.appeler<ReglageAppris[]>('beta.reglages');
      return (Array.isArray(reglages) ? reglages : []).find((r) => r.key === 'courrier_annonce.canal') ?? null;
    },
    async ajouter(phase, libelle, apresEtapeId) {
      const enTete = apresEtapeId === EN_TETE;
      const creee = await client.appeler<{ id: string }>('procedure.ajouter', {
        parametres,
        corps: { phase, label: libelle, afterStepId: enTete ? null : apresEtapeId },
      });
      if (!enTete) return;
      // Deux écritures non atomiques : l'ordre est relu APRÈS l'ajout ; aucune écriture inutile si l'étape est seule ou déjà en
      // tête ; un échec dit la vérité (l'étape est à la fin), jamais un succès « en tête ».
      try {
        const ids = (await client.appeler<{ id: string }[]>('procedure.etapes', { parametres })).map((e) => e.id);
        if (!ids.includes(creee.id)) throw new Error(MESSAGE_TETE_NON_PLACEE);
        if (ids[0] === creee.id) return;
        await client.appeler('procedure.reordonner', { parametres, corps: { stepIds: [creee.id, ...ids.filter((x) => x !== creee.id)] } });
      } catch {
        throw new Error(MESSAGE_TETE_NON_PLACEE);
      }
    },
    async sauter(etapeId, motif) {
      if (!motifValide(motif)) throw new Error(MESSAGE_MOTIF);
      await client.appeler('procedure.sauter', { parametres: { dossierId, etapeId }, corps: { reason: motif.trim() } });
    },
    async reordonner(ids) {
      await client.appeler('procedure.reordonner', { parametres, corps: { stepIds: [...ids] } });
    },
  };
}
